import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useEffect, lazy, Suspense } from "react";

// Iter 130.ax · Code-splitting de rutas.
// Solo se cargan eager los flujos públicos críticos (login, register, portales). El
// resto se carga lazy y se renderiza dentro de <Suspense>. Esto reduce el bundle
// inicial y elimina el problema histórico de "Loading chunk failed" en redeploys.

// ── Eager (públicas / críticas) ──────────────────────────────────────────
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PortalPublico from "@/pages/PortalPublico";
import PublicInspector from "@/pages/PublicInspector";
import FirmaPublicaPage from "@/pages/FirmaPublicaPage";
import PropuestaPublicaPage from "@/pages/PropuestaPublicaPage";
import FirmaDocPublicaPage from "@/pages/FirmaDocPublicaPage";
import Dashboard from "@/pages/Dashboard";

// ── Lazy (todo lo demás) ─────────────────────────────────────────────────
const Avales = lazy(() => import("@/pages/Avales"));
const Biblioteca = lazy(() => import("@/pages/Biblioteca"));
const DocumentacionEmpresa = lazy(() => import("@/pages/DocumentacionEmpresa"));
const TasasUrbanisticas = lazy(() => import("@/pages/TasasUrbanisticas"));
const AnalisisProyecto = lazy(() => import("@/pages/AnalisisProyecto"));
const DocumentalObra = lazy(() => import("@/pages/DocumentalObra"));
const DocumentalObraCockpit = lazy(() => import("@/pages/DocumentalObraCockpit"));
const DocumentalObraHub = lazy(() => import("@/pages/DocumentalObraHub"));
const DocumentalPlanificacionGlobal = lazy(() => import("@/pages/DocumentalPlanificacionGlobal"));
const Procedimientos = lazy(() => import("@/pages/Procedimientos"));
const MisProcedimientos = lazy(() => import("@/pages/MisProcedimientos"));
const MisTareasWorkflow = lazy(() => import("@/pages/MisTareasWorkflow"));
const WorkflowDashboard = lazy(() => import("@/pages/WorkflowDashboard"));
const Notificaciones = lazy(() => import("@/pages/Notificaciones"));
const CuentasPagar = lazy(() => import("@/pages/CuentasPagar"));
const CuentasCobrar = lazy(() => import("@/pages/CuentasCobrar"));
const Tesoreria = lazy(() => import("@/pages/Tesoreria"));
const Facturcal = lazy(() => import("@/pages/Facturcal"));
const Morosidad = lazy(() => import("@/pages/Morosidad"));
const CfoPanel = lazy(() => import("@/pages/CfoPanel"));
const ChatInterno = lazy(() => import("@/pages/ChatInterno"));
const SedeReglas = lazy(() => import("@/pages/SedeReglas"));
const PagosApp = lazy(() => import("@/pages/PagosApp"));
const TarjetasEmpresa = lazy(() => import("@/pages/TarjetasEmpresa"));
const PagoGracias = lazy(() => import("@/pages/PagoGracias"));
const MiCartera = lazy(() => import("@/pages/MiCartera"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const PuestaEnMarcha = lazy(() => import("@/pages/PuestaEnMarcha"));
const FinancieroOps = lazy(() => import("@/pages/FinancieroOps"));
const Flota = lazy(() => import("@/pages/Flota"));
const Plantilla = lazy(() => import("@/pages/Plantilla"));
const RolesOperativos = lazy(() => import("@/pages/RolesOperativos"));
const Microcompras = lazy(() => import("@/pages/Microcompras"));
const ProduccionSubcontratas = lazy(() => import("@/pages/ProduccionSubcontratas"));
const LegalConfig = lazy(() => import("@/pages/LegalConfig"));
const Gerencia = lazy(() => import("@/pages/Gerencia"));
const AprobacionFacturas = lazy(() => import("@/pages/AprobacionFacturas"));
const DehuCertificado = lazy(() => import("@/pages/DehuCertificado"));
const BuzonDEHu = lazy(() => import("@/pages/BuzonDEHu"));
const PanelFirmas = lazy(() => import("@/pages/PanelFirmas"));
const FacturasRecibidas = lazy(() => import("@/pages/FacturasRecibidas"));
const EmailInbox = lazy(() => import("@/pages/EmailInbox"));
const PlazosLegales = lazy(() => import("@/pages/PlazosLegales"));
const RRHHAmpliado = lazy(() => import("@/pages/RRHHAmpliado"));
const MisDocumentos = lazy(() => import("@/pages/MisDocumentos"));
const GestionObra = lazy(() => import("@/pages/GestionObra"));
// WA0243 · página SOP legado eliminada (ruta /sop redirige a /procedimientos)
const Garantias = lazy(() => import("@/pages/Garantias"));
const AvalesGarantias = lazy(() => import("@/pages/AvalesGarantias"));
const Admin = lazy(() => import("@/pages/Admin"));
const Facturacion = lazy(() => import("@/pages/Facturacion"));
const Bancos = lazy(() => import("@/pages/Bancos"));
const FiscalSL = lazy(() => import("@/pages/FiscalSL"));
const LibroDiario = lazy(() => import("@/pages/LibroDiario"));
const DashboardFiscal = lazy(() => import("@/pages/DashboardFiscal"));
const Licitaciones = lazy(() => import("@/pages/Licitaciones"));
const LicitacionesHub = lazy(() => import("@/pages/LicitacionesHub"));
const PliegosDescargados = lazy(() => import("@/pages/PliegosDescargados"));
const LicitacionCockpit = lazy(() => import("@/pages/LicitacionCockpit"));
const Juridico = lazy(() => import("@/pages/Juridico"));
const Comercial = lazy(() => import("@/pages/Comercial"));
const ComercialCRM = lazy(() => import("@/pages/ComercialCRM"));
const KanbanGlobal = lazy(() => import("@/pages/KanbanGlobal"));
const LegalModulo = lazy(() => import("@/pages/LegalModulo"));
const NotificacionesLegales = lazy(() => import("@/pages/NotificacionesLegales"));
const Expedientes = lazy(() => import("@/pages/Expedientes"));
const RRHH = lazy(() => import("@/pages/RRHH"));
const Adquisiciones = lazy(() => import("@/pages/Adquisiciones"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const MiEmpresa = lazy(() => import("@/pages/MiEmpresa"));
const Alertas = lazy(() => import("@/pages/Alertas"));
const Solvencia = lazy(() => import("@/pages/Solvencia"));
const AuxiliarLegal = lazy(() => import("@/pages/AuxiliarLegal"));
const CentroMando = lazy(() => import("@/pages/CentroMando"));
const CrmLicitaciones = lazy(() => import("@/pages/CrmLicitaciones"));
const CalendarioLicitaciones = lazy(() => import("@/pages/CalendarioLicitaciones"));
const NuevaLicitacion = lazy(() => import("@/pages/NuevaLicitacion"));
const SeguimientoLicitaciones = lazy(() => import("@/pages/SeguimientoLicitaciones"));
const ComunicacionesContratacion = lazy(() => import("@/pages/ComunicacionesContratacion"));
const CumplimentarAnexos = lazy(() => import("@/pages/CumplimentarAnexos"));
const MemoriaTecnica = lazy(() => import("@/pages/MemoriaTecnica"));
const MiDocumentacion = lazy(() => import("@/pages/MiDocumentacion"));
const AnaliticasLicitaciones = lazy(() => import("@/pages/AnaliticasLicitaciones"));
const MisBusquedas = lazy(() => import("@/pages/MisBusquedas"));
const ClientesPortal = lazy(() => import("@/pages/ClientesPortal"));
const ComunicacionInterna = lazy(() => import("@/pages/ComunicacionInterna"));
const ObraDashboard = lazy(() => import("@/pages/ObraDashboard"));
const RevisionProyectoPage = lazy(() => import("@/pages/RevisionProyectoPage"));
const GaleriaObraPage = lazy(() => import("@/pages/GaleriaObraPage"));
const PreparacionInicioPage = lazy(() => import("@/pages/PreparacionInicioPage"));
const PreReplanteoPage = lazy(() => import("@/pages/PreReplanteoPage"));
const PSSGenerator = lazy(() => import("@/pages/PSSGenerator"));
const PSS = lazy(() => import("@/pages/PSS"));
const PSSDetalle = lazy(() => import("@/pages/PSSDetalle"));
const Bitacora = lazy(() => import("@/pages/Bitacora"));
const CAE = lazy(() => import("@/pages/CAE"));
const CAEGlobal = lazy(() => import("@/pages/CAEGlobal"));
const Calendario = lazy(() => import("@/pages/Calendario"));
const SalaReuniones = lazy(() => import("@/pages/SalaReuniones"));
const MeetingRoom = lazy(() => import("@/pages/MeetingRoom"));
const MiPersonal = lazy(() => import("@/pages/MiPersonal"));
const MiFlota = lazy(() => import("@/pages/MiFlota"));


// Iter 131.j · Reset scroll al cambiar de ruta (módulo se abre arriba, no abajo)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

/**
 * Iter 130.an · Anti-pantalla-en-blanco: limpieza proactiva de Service Workers
 * y caches stale. Se ejecuta UNA VEZ al arrancar la app.
 */
function SwAndChunkGuard() {
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => { r.unregister().catch(() => {}); });
      }).catch(() => {});
    }
    const FLAG = "_legatum_chunk_reload_flag";
    const onChunkError = (msg) => {
      if (!/loading chunk|loading css chunk|ChunkLoadError/i.test(msg || "")) return;
      try {
        if (sessionStorage.getItem(FLAG)) return;
        sessionStorage.setItem(FLAG, "1");
      } catch (_e) { /* swallow */ }
      const u = new URL(window.location.href);
      u.searchParams.set("_cl", String(Date.now()));
      window.location.replace(u.toString());
    };
    const onError = (e) => onChunkError(String(e?.message || e?.error?.message || ""));
    const onRejection = (e) => onChunkError(String(e?.reason?.message || e?.reason || ""));
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[var(--legatum-bg)]" data-testid="route-fallback">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-full border-2 border-[var(--legatum-border)] border-t-[#C28A2A] animate-spin" />
        <p className="text-xs uppercase tracking-widest font-bold text-[var(--legatum-text-muted)]">Cargando módulo…</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SwAndChunkGuard />
            <AuthProvider>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  {/* Public */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/portal/:token" element={<PortalPublico />} />
                  <Route path="/inspector/:token" element={<PublicInspector />} />
                  <Route path="/firma/:token" element={<FirmaPublicaPage />} />
                  <Route path="/propuesta/:token" element={<PropuestaPublicaPage />} />
                  <Route path="/firmar-doc/:token" element={<FirmaDocPublicaPage />} />
                  <Route path="/pago/gracias" element={<PagoGracias />} />
                  <Route path="/pago/cancelado" element={<PagoGracias />} />

                  {/* Private */}
                  {/* WA0256 · Landing = Dashboard (el usuario pidió eliminar el calendario de la pantalla principal). Calendario sigue en /calendario */}
                  <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/avales" element={<ProtectedRoute><Avales /></ProtectedRoute>} />
                  <Route path="/biblioteca" element={<ProtectedRoute><Biblioteca /></ProtectedRoute>} />
                  <Route path="/documentacion-empresa" element={<ProtectedRoute><DocumentacionEmpresa /></ProtectedRoute>} />
                  <Route path="/tasas-urbanisticas" element={<ProtectedRoute><TasasUrbanisticas /></ProtectedRoute>} />
                  <Route path="/analisis" element={<ProtectedRoute><AnalisisProyecto /></ProtectedRoute>} />
                  <Route path="/documental-obra" element={<ProtectedRoute><DocumentalObra /></ProtectedRoute>} />
                  <Route path="/documental-obra/planificacion" element={<ProtectedRoute><DocumentalPlanificacionGlobal /></ProtectedRoute>} />
                  <Route path="/procedimientos" element={<ProtectedRoute><Procedimientos /></ProtectedRoute>} />
                  <Route path="/mis-procedimientos" element={<ProtectedRoute><MisProcedimientos /></ProtectedRoute>} />
                  <Route path="/mis-tareas-workflow" element={<ProtectedRoute><MisTareasWorkflow /></ProtectedRoute>} />
                  <Route path="/workflow-dashboard" element={<ProtectedRoute><WorkflowDashboard /></ProtectedRoute>} />
                  <Route path="/notificaciones-kratos" element={<Navigate to="/legal" replace />} />
                  <Route path="/financiero/cuentas-pagar" element={<ProtectedRoute><CuentasPagar /></ProtectedRoute>} />
                  <Route path="/financiero/cuentas-cobrar" element={<ProtectedRoute><CuentasCobrar /></ProtectedRoute>} />
                  <Route path="/financiero/tesoreria" element={<ProtectedRoute><Tesoreria /></ProtectedRoute>} />
                  <Route path="/financiero/facturcal" element={<ProtectedRoute><Facturcal /></ProtectedRoute>} />
                  <Route path="/financiero/morosidad" element={<ProtectedRoute><Morosidad /></ProtectedRoute>} />
                  <Route path="/financiero/cfo" element={<ProtectedRoute><CfoPanel /></ProtectedRoute>} />
                  <Route path="/chat" element={<ProtectedRoute><ChatInterno /></ProtectedRoute>} />
                  <Route path="/sede-reglas" element={<ProtectedRoute><SedeReglas /></ProtectedRoute>} />
                  <Route path="/pagos" element={<ProtectedRoute><PagosApp /></ProtectedRoute>} />
                  <Route path="/tarjetas" element={<ProtectedRoute><TarjetasEmpresa /></ProtectedRoute>} />
                  <Route path="/cartera" element={<ProtectedRoute><MiCartera /></ProtectedRoute>} />
                  <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                  <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
                  <Route path="/puesta-en-marcha" element={<ProtectedRoute><PuestaEnMarcha /></ProtectedRoute>} />
                  <Route path="/financiero/operaciones" element={<ProtectedRoute><FinancieroOps /></ProtectedRoute>} />
                  <Route path="/rrhh/flota" element={<ProtectedRoute><Flota /></ProtectedRoute>} />
                  <Route path="/rrhh/plantilla" element={<ProtectedRoute><Plantilla /></ProtectedRoute>} />
                  <Route path="/rrhh/operativos" element={<ProtectedRoute><RolesOperativos /></ProtectedRoute>} />
                  <Route path="/microcompras" element={<ProtectedRoute><Microcompras /></ProtectedRoute>} />
                  <Route path="/produccion-subcontratas" element={<ProtectedRoute><ProduccionSubcontratas /></ProtectedRoute>} />
                  <Route path="/admin/legal-config" element={<ProtectedRoute><LegalConfig /></ProtectedRoute>} />
                  <Route path="/gerencia" element={<ProtectedRoute><Gerencia /></ProtectedRoute>} />
                  <Route path="/aprobacion-facturas" element={<ProtectedRoute><AprobacionFacturas /></ProtectedRoute>} />
                  <Route path="/facturas-recibidas" element={<ProtectedRoute><FacturasRecibidas /></ProtectedRoute>} />
                  <Route path="/admin/dehu-certificado" element={<ProtectedRoute><DehuCertificado /></ProtectedRoute>} />
                  <Route path="/admin/email-inbox" element={<ProtectedRoute><EmailInbox /></ProtectedRoute>} />
                  <Route path="/correo" element={<ProtectedRoute><EmailInbox /></ProtectedRoute>} />
                  <Route path="/plazos-legales" element={<ProtectedRoute><PlazosLegales /></ProtectedRoute>} />
                  <Route path="/documental-obra/:obraId" element={<ProtectedRoute><DocumentalObraCockpit /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/documental" element={<ProtectedRoute><DocumentalObraHub /></ProtectedRoute>} />
                  <Route path="/rrhh/avanzado" element={<ProtectedRoute><RRHHAmpliado /></ProtectedRoute>} />
                  <Route path="/mis-documentos" element={<ProtectedRoute><MisDocumentos /></ProtectedRoute>} />
                  <Route path="/sala-reuniones" element={<ProtectedRoute><SalaReuniones /></ProtectedRoute>} />
                  <Route path="/sala-reuniones/:meetingId" element={<ProtectedRoute><MeetingRoom /></ProtectedRoute>} />
                  <Route path="/obras" element={<ProtectedRoute><GestionObra /></ProtectedRoute>} />
                  <Route path="/obras/:obraId" element={<ProtectedRoute><ObraDashboard /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/revision-proyecto" element={<ProtectedRoute><RevisionProyectoPage /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/galeria" element={<ProtectedRoute><GaleriaObraPage /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/preparacion-inicio" element={<ProtectedRoute><PreparacionInicioPage /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/pre-replanteo" element={<ProtectedRoute><PreReplanteoPage /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/pss" element={<ProtectedRoute><PSSGenerator /></ProtectedRoute>} />
                  <Route path="/pss" element={<ProtectedRoute><PSS /></ProtectedRoute>} />
                  <Route path="/pss/:id" element={<ProtectedRoute><PSSDetalle /></ProtectedRoute>} />
                  <Route path="/bitacora" element={<ProtectedRoute><Bitacora /></ProtectedRoute>} />
                  <Route path="/obras/:obraId/cae" element={<ProtectedRoute><CAE /></ProtectedRoute>} />
                  <Route path="/cae" element={<ProtectedRoute><CAEGlobal /></ProtectedRoute>} />
                  <Route path="/calendario" element={<ProtectedRoute><Calendario /></ProtectedRoute>} />
                  <Route path="/compras" element={<Navigate to="/proveedores" replace />} />
                  <Route path="/facturacion" element={<ProtectedRoute><Facturacion /></ProtectedRoute>} />
                  <Route path="/bancos" element={<ProtectedRoute><Bancos /></ProtectedRoute>} />
                  <Route path="/fiscal-sl" element={<ProtectedRoute><FiscalSL /></ProtectedRoute>} />
                  <Route path="/contabilidad/libro-diario" element={<ProtectedRoute><LibroDiario /></ProtectedRoute>} />
                  <Route path="/contabilidad/fiscal-360" element={<ProtectedRoute><DashboardFiscal /></ProtectedRoute>} />
                  <Route path="/licitaciones" element={<ProtectedRoute><Licitaciones /></ProtectedRoute>} />
                  <Route path="/licitaciones/inicio" element={<ProtectedRoute><LicitacionesHub /></ProtectedRoute>} />
                  <Route path="/licitaciones/buscador" element={<ProtectedRoute><Licitaciones /></ProtectedRoute>} />
                  <Route path="/licitaciones/pliegos" element={<ProtectedRoute><PliegosDescargados /></ProtectedRoute>} />
                  <Route path="/licitaciones/cockpit/:lid" element={<ProtectedRoute><LicitacionCockpit /></ProtectedRoute>} />
                  <Route path="/juridico" element={<Navigate to="/legal" replace />} />
                  <Route path="/comercial" element={<ProtectedRoute><ComercialCRM /></ProtectedRoute>} />
                  <Route path="/kanban-global" element={<ProtectedRoute><KanbanGlobal /></ProtectedRoute>} />
                  <Route path="/legal" element={<ProtectedRoute><LegalModulo /></ProtectedRoute>} />
                  <Route path="/dehu" element={<ProtectedRoute><BuzonDEHu /></ProtectedRoute>} />
                  <Route path="/firmas" element={<ProtectedRoute><PanelFirmas /></ProtectedRoute>} />
                  <Route path="/legal/notificaciones" element={<ProtectedRoute><NotificacionesLegales /></ProtectedRoute>} />
                  <Route path="/comercial-adjudicadas" element={<ProtectedRoute><Comercial /></ProtectedRoute>} />
                  <Route path="/expedientes" element={<ProtectedRoute><Expedientes /></ProtectedRoute>} />
                  <Route path="/rrhh" element={<ProtectedRoute><RRHH /></ProtectedRoute>} />
                  <Route path="/subcontratas" element={<Navigate to="/proveedores" replace />} />
                  <Route path="/adquisiciones" element={<Navigate to="/proveedores" replace />} />
                  <Route path="/proveedores" element={<ProtectedRoute><Adquisiciones /></ProtectedRoute>} />
                  <Route path="/audit-log" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
                  <Route path="/mi-empresa" element={<ProtectedRoute><MiEmpresa /></ProtectedRoute>} />
                  <Route path="/alertas-placsp" element={<ProtectedRoute><Alertas /></ProtectedRoute>} />
                  <Route path="/solvencia" element={<ProtectedRoute><Solvencia /></ProtectedRoute>} />
                  <Route path="/auxiliar-legal" element={<ProtectedRoute><AuxiliarLegal /></ProtectedRoute>} />
                  <Route path="/centro-mando" element={<ProtectedRoute><CentroMando /></ProtectedRoute>} />
                  <Route path="/crm-licitaciones" element={<ProtectedRoute><CrmLicitaciones /></ProtectedRoute>} />
                  <Route path="/calendario-licitaciones" element={<ProtectedRoute><CalendarioLicitaciones /></ProtectedRoute>} />
                  <Route path="/nueva-licitacion" element={<ProtectedRoute><NuevaLicitacion /></ProtectedRoute>} />
                  <Route path="/seguimiento-licitaciones" element={<ProtectedRoute><SeguimientoLicitaciones /></ProtectedRoute>} />
                  <Route path="/comunicaciones-contratacion" element={<ProtectedRoute><ComunicacionesContratacion /></ProtectedRoute>} />
                  <Route path="/cumplimentar-anexos" element={<ProtectedRoute><CumplimentarAnexos /></ProtectedRoute>} />
                  <Route path="/memoria-tecnica" element={<ProtectedRoute><MemoriaTecnica /></ProtectedRoute>} />
                  <Route path="/mi-documentacion" element={<ProtectedRoute><MiDocumentacion /></ProtectedRoute>} />
                  <Route path="/analiticas-licitaciones" element={<ProtectedRoute><AnaliticasLicitaciones /></ProtectedRoute>} />
                  <Route path="/mis-busquedas" element={<ProtectedRoute><MisBusquedas /></ProtectedRoute>} />
                  <Route path="/control-horario" element={<Navigate to="/rrhh" replace />} />
                  <Route path="/sop" element={<Navigate to="/procedimientos" replace />} />
                  <Route path="/garantias" element={<ProtectedRoute><Garantias /></ProtectedRoute>} />
                  <Route path="/avales-garantias" element={<ProtectedRoute><AvalesGarantias /></ProtectedRoute>} />
                  <Route path="/clientes" element={<ProtectedRoute><ClientesPortal /></ProtectedRoute>} />
                  <Route path="/comunicacion" element={<ProtectedRoute><ComunicacionInterna /></ProtectedRoute>} />
                  <Route path="/notificaciones" element={<Navigate to="/comunicacion" replace />} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/mi-personal" element={<ProtectedRoute><MiPersonal /></ProtectedRoute>} />
                  <Route path="/mi-flota" element={<ProtectedRoute><MiFlota /></ProtectedRoute>} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
